import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSectionSearch } from './SectionTabs';
import SPAButton from '../shared/SPAButton';
import ClassMap from './ClassMap';
import type { ClassroomRow, CourseRow, LaunchCourse } from './ClassMap';
import { InlineStat } from './Metric';

interface Unreachable {
  count: number;
  never_launched: number;
  sample: { id: number; name: string }[];
}

interface LmsOnly {
  count: number;
  unknown_to_tci: number;
  sample: { lms_id: string; student_id: number | null; name: string | null }[];
}

/** Distinct students — never summed from the class rows, whose rosters overlap. */
interface Totals {
  assigned: number;
  reachable: number;
  classes: number;
  courses: number;
}

export interface RosterPayload {
  classrooms?: ClassroomRow[];
  courses?: CourseRow[];
  totals?: Totals;
  unreachable?: Unreachable;
  lms_only?: LmsOnly;
  error?: string;
}

// AIDEV-NOTE: The names go behind a disclosure rather than into a grey sentence. They matter when
// someone is chasing one student and are noise the rest of the time, and inline they pushed the
// mapping — the thing you actually read this section for — below the fold.
const Names = ({ children, summary }: { children: React.ReactNode; summary: string }) => (
  <details className="tw-text-sm">
    <summary className="tw-cursor-pointer tw-text-spa-secondary hover:tw-text-spa-primary">
      {summary}
    </summary>
    <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-secondary">{children}</p>
  </details>
);

interface RosterPanelProps {
  assignmentId?: string;
  data?: RosterPayload;
  launchCourses?: Map<number, LaunchCourse[]>;
  onSelectCourse?: (courseId: string) => void;
}

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

const unreachableSummary = ({ count, never_launched: never }: Unreachable) => {
  const launched = never > 0 ? ` — ${never} never launched` : '';

  return `${plural(count, 'assigned student is', 'assigned students are')} in none of these courses${launched}`;
};

const lmsOnlySummary = ({ count, unknown_to_tci: unknown }: LmsOnly) => {
  const accounts = unknown > 0 ? ` — ${plural(unknown, 'has', 'have')} no TCI account` : '';

  return `${plural(count, 'student is', 'students are')} in an LMS course but not assigned this in TCI${accounts}`;
};

const RosterPanel = ({
  assignmentId, data, launchCourses, onSelectCourse,
}: RosterPanelProps) => {
  const navigate = useNavigate();
  const sectionSearch = useSectionSearch();

  if (data == null) return null;
  if (data.error != null) {
    return <p className="tw-m-0 tw-text-sm tw-text-spa-error">{data.error}</p>;
  }

  const classrooms = data.classrooms ?? [];
  const courses = data.courses ?? [];
  const { unreachable, lms_only: lmsOnly } = data;

  const assigned = data.totals?.assigned ?? 0;
  const reachable = data.totals?.reachable ?? 0;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-4">
        <div className="tw-flex tw-flex-wrap tw-gap-8">
          <InlineStat label="Students assigned" value={assigned} />
          <InlineStat label="Can receive a grade" value={reachable} />
          <InlineStat label="Cannot" tone="error" value={assigned - reachable} />
          <InlineStat label="In the LMS only" value={lmsOnly?.count ?? 0} />
        </div>

        {/* AIDEV-NOTE: A button beside the numbers it fixes, not a link under the table: as a trailing
            text link support missed it, and relinking a student is often the whole repair. */}
        {assignmentId != null && (
          <div className="tw-flex tw-max-w-xs tw-flex-col tw-items-end tw-gap-1 tw-text-right">
            <SPAButton size="sm" type="button" variant="outline" onClick={() => navigate(`students${sectionSearch}`)}>
              Edit LMS user links
            </SPAButton>
            <span className="tw-text-sm tw-text-spa-tertiary">
              Link or unlink which TCI student each LMS user is
            </span>
          </div>
        )}
      </div>

      {assignmentId != null && (
        <ClassMap
          assignmentId={assignmentId}
          classrooms={classrooms}
          courses={courses}
          launchCourses={launchCourses}
          onSelectCourse={onSelectCourse}
        />
      )}

      <div className="tw-flex tw-flex-col tw-gap-2">
        {unreachable != null && unreachable.count > 0 && (
          <Names
            summary={unreachableSummary(unreachable)}
          >
            {unreachable.sample.map(s => s.name).join(', ')}
            {unreachable.count > unreachable.sample.length && ' …'}
          </Names>
        )}

        {lmsOnly != null && lmsOnly.count > 0 && (
          <Names
            summary={lmsOnlySummary(lmsOnly)}
          >
            {lmsOnly.sample.map(s => s.name ?? `LMS user ${s.lms_id}`).join(', ')}
            {lmsOnly.count > lmsOnly.sample.length && ' …'}
          </Names>
        )}
      </div>
    </div>
  );
};

export default RosterPanel;
